import React, { useState, useEffect } from 'react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { getEmployeeTransactions, setAuthToken } from './services/api';
import { useAuth } from './components/AuthProvider';
import SpendingSummary from './components/SpendingSummary';
import FrequentItemsTable from './components/FrequentItemsTable';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorMessage from './components/ErrorMessage';
import LoginCard from './components/LoginCard';
import UserProfile from './components/UserProfile';
import './App.css';

function App() {
  const { isAuthenticated, user, accessToken, getAccessToken } = useAuth();
  const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Set auth token when it changes
  useEffect(() => {
    if (accessToken) {
      setAuthToken(accessToken);
    }
  }, [accessToken]);

  const processTransactionData = (transactions) => {
    if (!transactions || transactions.length === 0) {
      return {
        summary: {
          totalSpent: 0,
          transactionCount: 0,
          averagePerTransaction: 0,
          uniqueItems: 0
        },
        frequentItems: [],
        employeeName: null
      };
    }

    // Calculate summary
    const totalSpent = transactions.reduce((sum, transaction) => {
      return sum + parseFloat(transaction.total_cost_r || 0);
    }, 0);

    const transactionCount = transactions.length;
    const averagePerTransaction = transactionCount > 0 ? totalSpent / transactionCount : 0;
    
    // Get employee name from first transaction
    const employeeName = transactions[0]?.employee_name || null;

    // Calculate frequent items
    const itemStats = {};
    
    transactions.forEach(transaction => {
      const itemName = transaction.item;
      const quantity = parseInt(transaction.quantity || 1);
      const totalCost = parseFloat(transaction.total_cost_r || 0);
      const price = parseFloat(transaction.price_r || 0);

      if (!itemStats[itemName]) {
        itemStats[itemName] = {
          name: itemName,
          frequency: 0,
          totalQuantity: 0,
          totalSpent: 0,
          prices: []
        };
      }

      itemStats[itemName].frequency += 1;
      itemStats[itemName].totalQuantity += quantity;
      itemStats[itemName].totalSpent += totalCost;
      itemStats[itemName].prices.push(price);
    });

    // Convert to array and calculate averages
    const frequentItems = Object.values(itemStats).map(item => ({
      ...item,
      averagePrice: item.prices.length > 0 
        ? item.prices.reduce((sum, price) => sum + price, 0) / item.prices.length 
        : 0
    }));

    // Sort by frequency (descending) and take top 10
    frequentItems.sort((a, b) => b.frequency - a.frequency);
    const topFrequentItems = frequentItems.slice(0, 10);

    const uniqueItems = Object.keys(itemStats).length;

    return {
      summary: {
        totalSpent,
        transactionCount,
        averagePerTransaction,
        uniqueItems
      },
      frequentItems: topFrequentItems,
      employeeName
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user?.displayName) {
      setError('User information not available');
      return;
    }

    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date cannot be after end date');
      return;
    }

    setLoading(true);
    setError(null);
    setData(null);

    try {
      // Ensure we have a fresh token
      const token = await getAccessToken();
      if (token) {
        setAuthToken(token);
      }

      const response = await getEmployeeTransactions(user.displayName, startDate, endDate);
      const processedData = processTransactionData(response.transactions || []);
      setData(processedData);
    } catch (err) {
      console.error('Error fetching data:', err);
      
      let errorMessage = 'Failed to fetch transaction data. Please try again.';
      
      if (err.response) {
        // Server responded with error status
        if (err.response.status === 401) {
          errorMessage = 'Authentication failed. Please sign in again.';
        } else if (err.response.status === 403) {
          errorMessage = 'Access denied. You can only view your own data.';
        } else if (err.response.status === 404) {
          errorMessage = 'No data found for your account or date range.';
        } else if (err.response.status === 400) {
          errorMessage = 'Invalid request or date format.';
        } else if (err.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'Unable to connect to the server. Please check your internet connection.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    handleSubmit({ preventDefault: () => {} });
  };

  const setQuickDateRange = (days) => {
    const end = new Date();
    const start = subDays(end, days);
    setStartDate(format(start, 'yyyy-MM-dd'));
    setEndDate(format(end, 'yyyy-MM-dd'));
  };

  const setCurrentMonth = () => {
    const now = new Date();
    setStartDate(format(startOfMonth(now), 'yyyy-MM-dd'));
    setEndDate(format(endOfMonth(now), 'yyyy-MM-dd'));
  };

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="app">
        <div className="app-container">
          <header className="app-header">
            <h1>🍿 Snack Lookup</h1>
            <p>Track your snack bar spending</p>
          </header>
          <LoginCard />
        </div>
      </div>
    );
  }

  // Show main app if authenticated
  return (
    <div className="app">
      <div className="app-container">
        <header className="app-header">
          <h1>🍿 Snack Lookup</h1>
          <p>Track your snack bar spending</p>
        </header>

        <UserProfile />

        <div className="search-form-container">
          <form onSubmit={handleSubmit} className="search-form">
            <div className="date-range-group">
              <div className="form-group">
                <label htmlFor="startDate">Start Date</label>
                <input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="form-input"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="endDate">End Date</label>
                <input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="form-input"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="quick-dates">
              <button 
                type="button" 
                onClick={() => setQuickDateRange(7)}
                className="quick-date-btn"
                disabled={loading}
              >
                Last 7 Days
              </button>
              <button 
                type="button" 
                onClick={() => setQuickDateRange(30)}
                className="quick-date-btn"
                disabled={loading}
              >
                Last 30 Days
              </button>
              <button 
                type="button" 
                onClick={setCurrentMonth}
                className="quick-date-btn"
                disabled={loading}
              >
                This Month
              </button>
            </div>

            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading}
            >
              {loading ? 'Searching...' : 'Get My Spending Report'}
            </button>
          </form>
        </div>

        {loading && (
          <LoadingSpinner message="Fetching your transaction data..." />
        )}

        {error && (
          <ErrorMessage message={error} onRetry={handleRetry} />
        )}

        {data && !loading && !error && (
          <div className="results-container">
            <SpendingSummary 
              summary={data.summary}
              employeeName={data.employeeName}
              startDate={startDate}
              endDate={endDate}
            />
            <FrequentItemsTable items={data.frequentItems} />
          </div>
        )}

        {data && !loading && !error && data.summary.transactionCount === 0 && (
          <div className="no-transactions">
            <div className="no-transactions-icon">📭</div>
            <h3>No Transactions Found</h3>
            <p>No snack bar purchases were found for your account in the selected date range.</p>
            <p>Try selecting a different date range.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;